import React, {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import AppsIcon from '@atlaskit/icon/core/apps';
import ButtonItem from '@atlaskit/menu/button-item';
import { Box } from '@atlaskit/primitives/compiled/box';
import { components } from '@atlaskit/react-select/components';
import { type MenuListProps } from '@atlaskit/react-select/menu-list';
import { type OptionType, type StylesConfig } from '@atlaskit/select/types';
import { token } from '@atlaskit/tokens';
import { type AppStackItem, type AppStackItemProps } from '@atlassian/app-stack';
import { RovoSettingsModalSurface } from '@atlassian/rovo-settings-modal/common/utils/rovo-settings-config';
import { RovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal';
import { MoreAppsControl } from '@atlassian/search-common/more-apps-control';

import { type RovoLogoProps } from '../../../../../common/constants/icons';
import { type NounKeys } from '../../../../../common/constants/nouns';
import { type ProductKeys } from '../../../../../common/constants/product-keys';
import { useProductConfigs } from '../../../../../common/constants/products';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../../../../common/constants/schemas/3p-config';
import { AppFilterPopupSelect } from '../../../../../common/ui/filters/select-filter';
import { Control } from '../../../../../common/ui/filters/select-filter/control';
import { messages as oAuthMessages } from '../../../../../common/ui/oauth-warning-wrapper/messages';
import { ProductOrNounIcon } from '../../../../../common/ui/product-icon';
import { warmUpCodeSearchForHoveredProduct } from '../../../../../common/utils/code-search-warmup';
import { OAuthSource } from '../../../../../common/utils/events/oauth-events';
import { onAppFilterShowMoreClicked } from '../../../../../common/utils/events/on-app-filter-show-more-clicked';
import { shouldShowRefreshedFilterRow } from '../../../../../common/utils/experiments';
import { getWorkspaceNameFromProduct } from '../../../../../common/utils/get-workspace-name-from-product';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../../controllers/oauth';
import {
	useBootstrap,
	useCloudId,
	useCurrentUserId,
	useOrgId,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import { useLiteFilterSelect } from '../../lite-app-filter-bar/use-lite-filter-select';
import { useRefreshAppOptions } from '../apps/use-refresh-app-options';
import { FacepileIcon } from './facepile-icon';
import { messages } from './messages';

export const MORE_APPS_TEST_ID = 'search-page-refresh-filter-row-more-apps';

const MENU_WIDTH = 259;
const MENU_MAX_HEIGHT = 336;

const styles = cssMap({
	optionRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		width: '100%',
	},
	optionMain: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		overflow: 'hidden',
		minWidth: 0,
	},
	optionLabel: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		font: token('font.body'),
		color: token('color.text'),
		minWidth: 0,
	},
	connectText: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		color: token('color.link'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
	},
	footer: {
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border'),
		paddingBlock: token('space.050'),
	},
});

type MoreAppsSelectOption = {
	value: string;
	label: string;
	isSelected: boolean;
	isUnauthenticated: boolean;
	productKey: ProductKeys | NounKeys;
	index: number;
};

type OpenRovoSettingsModal = (options?: { defaultTab?: string }) => void;

const RovoSettingsModalOpenContext = createContext<OpenRovoSettingsModal | null>(null);

const MoreAppsOptionIcon = ({
	productOrNounKey,
	thirdPartyConfigs,
	size,
}: {
	productOrNounKey: ProductKeys | NounKeys | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	size: RovoLogoProps['size'];
}): React.JSX.Element => {
	const forgeIcon =
		productOrNounKey && isForgeConnectorProductKey(productOrNounKey)
			? thirdPartyConfigs[productOrNounKey]?.icon
			: undefined;

	return forgeIcon ? (
		forgeIcon({ label: '', size })
	) : (
		<ProductOrNounIcon label="" size={size} productOrNounKey={productOrNounKey} />
	);
};

const DiscoverMoreApps = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const openRovoSettingsModal = useContext(RovoSettingsModalOpenContext);

	if (!openRovoSettingsModal) {
		return null;
	}

	return (
		<ButtonItem
			iconBefore={<AppsIcon label="" />}
			onClick={() => openRovoSettingsModal({ defaultTab: 'discovery' })}
			testId={`${MORE_APPS_TEST_ID}--discover-more-apps`}
		>
			{formatMessage(messages.discoverMoreApps)}
		</ButtonItem>
	);
};

const MoreAppsMenuList = (props: MenuListProps<MoreAppsSelectOption>): React.JSX.Element => (
	<>
		<components.MenuList {...props} />
		<Box xcss={styles.footer}>
			<DiscoverMoreApps />
		</Box>
	</>
);

const MENU_COMPONENTS = { Control, MenuList: MoreAppsMenuList };

// Module-level so the object identity is stable: react-select memoises on referential equality, so
// an inline literal would invalidate its style cache on every render.
const MENU_STYLES: StylesConfig<MoreAppsSelectOption> = {
	option: (baseStyles) => ({ ...baseStyles, cursor: 'pointer' }),
};

type MoreAppsProps = {
	defaultIsOpen?: boolean;
};

const MoreAppsImpl = ({ defaultIsOpen }: MoreAppsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const attributesRef = useRef({ commonAttributes, nonPrivacySafeAttributes });
	useEffect(() => {
		attributesRef.current = { commonAttributes, nonPrivacySafeAttributes };
	}, [commonAttributes, nonPrivacySafeAttributes]);

	const [{ thirdPartyConfigs }] = useBootstrap();
	const products = useProductConfigs();
	const { visibleOptions, overflowOptions } = useRefreshAppOptions();
	const [searchInput, setSearchInput] = useState('');
	const isSearching = searchInput.trim().length > 0;
	const handleMenuClose = useCallback(() => setSearchInput(''), []);
	// Shared with the app tiles and the Lite pill bar so every app filter surface applies
	// selections the same way and fires the same click analytics.
	const handleSelect = useLiteFilterSelect({ isInShowMore: true });

	// Browsing shows only overflow; searching also includes the apps already visible in the row.
	// Keep the 1-based analytics index relative to the current candidate list, before text matching.
	const selectOptions = useMemo<MoreAppsSelectOption[]>(
		() =>
			(isSearching ? [...visibleOptions, ...overflowOptions] : overflowOptions).map(
				(option, position) => ({
					value: String(option.value),
					label: option.label,
					isSelected: option.isSelected,
					isUnauthenticated: Boolean(option.isUnauthenticated),
					productKey: option.value,
					index: position + 1,
				}),
			),
		[isSearching, visibleOptions, overflowOptions],
	);

	// `AppStack` takes components rather than elements. `size` is deliberately not forwarded:
	// FacepileIcon fixes every glyph at 20x20 itself rather than trusting AppStack's xsmall.
	const apps = useMemo<AppStackItem[]>(
		() =>
			overflowOptions.map((option) => ({
				component: ({ label }: AppStackItemProps) => (
					<FacepileIcon label={label} productOrNounKey={option.value} />
				),
				testId: `${MORE_APPS_TEST_ID}--app-stack-${option.value}`,
			})),
		[overflowOptions],
	);

	const [connectProduct, setConnectProduct] = useState<ProductKeys | undefined>(undefined);
	const { outboundAuthUrl } = useIsUserOAuthed(connectProduct);
	const connectProductName = connectProduct
		? getWorkspaceNameFromProduct(connectProduct, thirdPartyConfigs) ||
			String(products[connectProduct]?.displayName ?? '')
		: '';
	const { onAuthorize } = useOAuthHandlers({
		product: connectProduct,
		successFlagTitle: formatMessage(oAuthMessages.authorizationSuccess, {
			product: connectProductName,
		}),
		failFlagTitle: formatMessage(oAuthMessages.authorizationFail, { product: connectProductName }),
		outboundAuthUrl: outboundAuthUrl || '',
	});
	const onAuthorizeRef = useRef(onAuthorize);
	useEffect(() => {
		onAuthorizeRef.current = onAuthorize;
	}, [onAuthorize]);

	useEffect(() => {
		if (connectProduct && outboundAuthUrl) {
			onAuthorizeRef.current(OAuthSource.searchPageFilter);
			setConnectProduct(undefined);
		}
	}, [connectProduct, outboundAuthUrl]);

	const formatOptionLabel = useCallback(
		(option: OptionType) => {
			const opt = option as MoreAppsSelectOption;

			return (
				<Box
					xcss={styles.optionRow}
					onMouseEnter={() => warmUpCodeSearchForHoveredProduct(String(opt.productKey))}
				>
					<Box xcss={styles.optionMain}>
						<MoreAppsOptionIcon
							size="small"
							productOrNounKey={opt.productKey}
							thirdPartyConfigs={thirdPartyConfigs}
						/>
						<Box xcss={styles.optionLabel}>{opt.label}</Box>
					</Box>
					{opt.isUnauthenticated && (
						<Box xcss={styles.connectText}>{formatMessage(messages.connect)}</Box>
					)}
				</Box>
			);
		},
		[formatMessage, thirdPartyConfigs],
	);

	const onChange = useCallback(
		(value: OptionType | readonly OptionType[] | null) => {
			const selected = (Array.isArray(value) ? value[0] : value) as MoreAppsSelectOption | null;
			if (!selected) {
				return;
			}
			if (selected.isUnauthenticated) {
				setConnectProduct(selected.productKey as ProductKeys);
				return;
			}
			handleSelect(selected.productKey, selected.index);
		},
		[handleSelect],
	);

	const handleMenuOpen = useCallback(() => {
		fireAnalyticsEvent(
			onAppFilterShowMoreClicked({
				commonAttributes: attributesRef.current.commonAttributes,
				nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
				isConnected: true,
				section: 'filterResults',
			}),
		);
	}, [fireAnalyticsEvent]);

	const isOptionSelected = useCallback((option: MoreAppsSelectOption) => option.isSelected, []);

	const hasSelectedOverflow = overflowOptions.some((option) => option.isSelected);

	if (overflowOptions.length === 0) {
		return null;
	}

	return (
		<AppFilterPopupSelect<MoreAppsSelectOption>
			label={formatMessage(messages.moreApps)}
			placeholder={formatMessage(messages.findApps)}
			options={selectOptions}
			inputValue={searchInput}
			onInputChange={setSearchInput}
			isOptionSelected={isOptionSelected}
			onChange={onChange}
			onMenuOpen={handleMenuOpen}
			onMenuClose={handleMenuClose}
			formatOptionLabel={formatOptionLabel}
			defaultIsOpen={defaultIsOpen}
			isSearchable
			searchThreshold={-1}
			blurInputOnSelect={false}
			isClearable={false}
			closeMenuOnSelect
			shouldCloseMenuOnTab={false}
			minMenuWidth={MENU_WIDTH}
			maxMenuWidth={`${MENU_WIDTH}px`}
			maxMenuHeight={MENU_MAX_HEIGHT}
			menuPlacement="bottom"
			components={MENU_COMPONENTS}
			styles={MENU_STYLES}
			popperProps={{
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{ name: 'preventOverflow', enabled: false },
				],
			}}
			target={({ isOpen, ...targetProps }) => (
				<MoreAppsControl
					{...targetProps}
					apps={apps}
					aria-haspopup="true"
					aria-expanded={isOpen}
					isSelected={isOpen || hasSelectedOverflow}
					testId={MORE_APPS_TEST_ID}
				/>
			)}
		/>
	);
};

const MoreAppsRovoSettingsModal = ({ children }: PropsWithChildren): React.JSX.Element => {
	const [primaryProduct] = usePrimaryProduct();
	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const accountId = useCurrentUserId();

	if (!cloudId || !primaryProduct) {
		return <>{children}</>;
	}

	return (
		<RovoSettingsModal
			product={primaryProduct}
			cloudId={cloudId}
			accountId={accountId ?? undefined}
			orgId={orgId}
			surface={RovoSettingsModalSurface.SEARCH_FPS_CONNECT_APPS_BUTTON}
		>
			{({ open }) => (
				<RovoSettingsModalOpenContext.Provider value={open}>
					{children}
				</RovoSettingsModalOpenContext.Provider>
			)}
		</RovoSettingsModal>
	);
};

const MoreAppsWithFlags = ({ defaultIsOpen }: MoreAppsProps): React.JSX.Element => (
	<FlagsProvider>
		<MoreAppsRovoSettingsModal>
			<MoreAppsImpl defaultIsOpen={defaultIsOpen} />
		</MoreAppsRovoSettingsModal>
	</FlagsProvider>
);

// Inner component, not an early return after hooks, so nothing runs when ineligible.
export const MoreApps = ({ defaultIsOpen }: MoreAppsProps = {}): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();

	if (!shouldShowRefreshedFilterRow(primaryProduct)) {
		return null;
	}

	return <MoreAppsWithFlags defaultIsOpen={defaultIsOpen} />;
};
